<template>
  <div>
    <b-modal
      v-model="modalShow"
      @hidden="$emit('hidden')"
      size="lg"
    >
      <template #modal-header="{ close }">
        <div class="d-flex align-items-center flex-fill">
          <h5 class="mb-0">Dividir Vínculo - {{ workRecord.companyName }}</h5>

          <b-button class="ml-auto" size="sm" variant="link" @click="close()">
            <i class="fa fa-times" aria-hidden="true"></i>
          </b-button>
        </div>
      </template>

      <template #default>
        <div class="alert alert-info">
          <i class="fa fa-info-circle mr-2" aria-hidden="true"></i>
          Esta ação irá dividir o vínculo atual em dois períodos. O primeiro período será encerrado na data especificada e um novo vínculo será criado a partir do dia seguinte.
        </div>

        <div class="row">
          <div class="col-md-12">
            <b-form-group
              label="Data de divisão"
              label-for="division-date"
              description="Selecione a data em que o vínculo será dividido"
            >
              <InputDatePicker 
                v-model="form.divisionDate" 
                :min="workRecord.ingressDate"
                :max="workRecord.egressDate"
              />
            </b-form-group>
          </div>

          <div class="col-md-12">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Resumo da divisão</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <h6 class="text-info">Primeiro período</h6>
                    <p class="mb-1"><strong>Empresa:</strong> {{ workRecord.companyName }}</p>
                    <p class="mb-1"><strong>Início:</strong> {{ formatDate(workRecord.ingressDate) }}</p>
                    <p class="mb-1"><strong>Fim:</strong> {{ form.divisionDate || 'Data a ser definida' }}</p>
                  </div>
                  <div class="col-md-6">
                    <h6 class="text-success">Segundo período</h6>
                    <p class="mb-1"><strong>Empresa:</strong> {{ workRecord.companyName }}</p>
                    <p class="mb-1"><strong>Início:</strong> {{ getNextDay(form.divisionDate) || 'Data a ser definida' }}</p>
                    <p class="mb-1"><strong>Fim:</strong> {{ formatDate(workRecord.egressDate) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="primary"
          @click="handleDivideWorkRecord"
          :disabled="isSaving || !form.divisionDate"
        >
          <i class="fa fa-scissors mr-1" aria-hidden="true"></i>
          Dividir Vínculo
        </b-button>

        <b-button
          variant="outline-primary"
          @click="cancel()"
        >
          Cancelar
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="showDeductionWarning"
      title="Atenção - Deduções"
      @ok="confirmDivide"
      @cancel="cancelDivide"
      ok-title="Continuar"
      cancel-title="Cancelar"
      ok-variant="warning"
    >
      <div class="alert alert-warning">
        <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>
        <strong>Atenção!</strong> Este registro possui deduções configuradas.
      </div>
      <p>
        Ao dividir o vínculo, as deduções serão zeradas em ambos os períodos criados.
      </p>
      <p>
        <strong>Deduções atuais:</strong>
      </p>
      <ul>
        <li v-if="workRecord.deduction1 > 0">Até 16/12/1998: {{ workRecord.deduction1 }} dias</li>
        <li v-if="workRecord.deduction2 > 0">Até 13/11/2019: {{ workRecord.deduction2 }} dias</li>
        <li v-if="workRecord.deduction3 > 0">A partir de 14/11/2019: {{ workRecord.deduction3 }} dias</li>
      </ul>
      <p>
        <strong>Deseja continuar com a divisão?</strong>
      </p>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import InputDatePicker from '@/components/InputDatePicker.vue';
import moment from 'moment';

export default {
  name: 'ModalDivideWorkRecord',

  components: {
    InputDatePicker,
  },

  props: {
    workRecord: {
      type: Object,
      required: true,
    },
    parentId: {
      type: Number,
      required: true,
    },
  },

  data: () => ({
    isSaving: false,
    modalShow: true,
    showDeductionWarning: false,
    form: {
      divisionDate: '',
    },
  }),

  computed: {
    hasDeductions() {
      return (this.workRecord.deduction1 && this.workRecord.deduction1 > 0) ||
             (this.workRecord.deduction2 && this.workRecord.deduction2 > 0) ||
             (this.workRecord.deduction3 && this.workRecord.deduction3 > 0);
    },
  },

  methods: {
    formatDate(date) {
      return moment(date).utc().format('DD/MM/YYYY');
    },

    getNextDay(date) {
      if (!date) return '';
      const m = moment(date, 'DD/MM/YYYY', true);
      if (!m.isValid()) return '';
      return m.add(1, 'day').format('DD/MM/YYYY');
    },

    handleDivideWorkRecord() {
      if (!this.form.divisionDate) {
        this.$toast.open({
          message: 'Selecione uma data de divisão',
          type: 'error',
          position: 'top-right'
        });
        return;
      }

      if (this.hasDeductions) {
        this.showDeductionWarning = true;
      } else {
        this.divideWorkRecord();
      }
    },

    async confirmDivide() {
      this.showDeductionWarning = false;
      await this.divideWorkRecord();
    },

    cancelDivide() {
      this.showDeductionWarning = false;
    },

    async divideWorkRecord() {
      this.isSaving = true;

      try {
        const divisionDate = moment(this.form.divisionDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        
        await axios.post(`/work-records/${this.workRecord.id}/divide`, { divisionDate });

        this.$toast.open({
          message: 'Vínculo dividido com sucesso',
          type: 'success',
          position: 'top-right'
        });

        this.$emit('save');
        this.$emit('hidden');
      } catch (error) {
        console.error('Error dividing work record:', error);
        this.$toast.open({
          message: 'Erro ao dividir vínculo',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.isSaving = false;
      }
    },
  }
}
</script>

<style scoped>
</style> 